import React from "react";
import styled from "styled-components";
import DOMPurify from "dompurify";

function ServiceCard({ icon, title, description, variant }) {
  const sanitizedDescription = DOMPurify.sanitize(description);

  return (
    <Card className="service-card" $variant={variant}>
      <IconWrapper>
        <CardIcon src={icon} alt="" loading="lazy" />
      </IconWrapper>
      <CardContent>
        <CardTitle>{title}</CardTitle>
        <CardDescription dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
      </CardContent>
    </Card>
  );
}

const Card = styled.article`
  border-radius: var(--border-radius);
  background: var(--card-gradient);
  display: flex;
  min-width: 240px;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;
  width: 317px;
  padding: 45px 40px;
  position: relative;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: var(--card-shadow);
  border: 1px solid rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(5px);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--card-hover-shadow);
    background: linear-gradient(145deg, #ffffff, #f0f7ff);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    z-index: -1;
    background: linear-gradient(315deg, var(--primary-color), var(--secondary-color));
    border-radius: calc(var(--border-radius) + 2px);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover:before {
    opacity: 0.3;
  }
  
  @media (max-width: 991px) {
    padding: 30px 20px;
  }
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(145deg, #ffffff, #f0f7ff);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.07);
  margin-bottom: 20px;
  position: relative;
  overflow: hidden;
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(145deg, rgba(255,255,255,0.5), rgba(255,255,255,0));
    border-radius: 50%;
  }
`;

const CardIcon = styled.img`
  aspect-ratio: 1;
  object-fit: contain;
  width: 40px;
  z-index: 2;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  color: var(--text-color);
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 22px;
  position: relative;
  letter-spacing: -0.5px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
    border-radius: 2px;
  }
`;

const CardDescription = styled.p`
  color: var(--light-text);
  font-size: 17px;
  font-weight: 400;
  line-height: 1.8;
  margin-top: 20px;
  
  .span-bold {
    color: var(--primary-color);
    font-weight: 700;
    font-size: 1.05em;
  }
  
  .span-dark {
    color: var(--text-color);
    font-weight: 600;
    font-size: 1.05em;
  }
  
  .span-bold.span-dark {
    color: var(--primary-color);
    font-weight: 700;
    font-size: 1.05em;
  }
`;

export default ServiceCard;
